<template>
  <!-- Lesson phase: teach a handful of words before the exercises so demo
       visitors aren't tested on script they've never seen. Once the mini-lesson
       is done (or if there's no vocab to show), we hand off to PracticeSession,
       which owns the exercises and the sign-up call to action. -->
  <div v-if="phase === 'lesson'" class="min-h-screen bg-gray-50 dark:bg-gray-900">
    <header class="bg-emerald-600 text-white py-6 px-4">
      <div class="max-w-3xl mx-auto">
        <a :href="backUrl" class="text-emerald-200 hover:text-white text-sm">&larr; Back to home</a>
        <h1 class="text-2xl font-bold mt-1">{{ unitTitle ? `Free demo · ${unitTitle}` : 'Free demo lesson' }}</h1>
      </div>
    </header>

    <main class="max-w-3xl mx-auto px-4 py-6">
      <div v-if="loading" class="text-center py-16">
        <div class="inline-block animate-spin rounded-full h-8 w-8 border-4 border-emerald-200 border-t-emerald-600"></div>
        <p class="mt-4 text-gray-500">Loading lesson...</p>
      </div>
      <template v-else>
        <LearnMode
          :vocab="vocab"
          :grammar="[]"
          :demo-mode="true"
          @done="startPractice"
          @progress="onLessonProgress"
        />
        <!-- Escape hatch for impatient visitors: jumping into exercises early is
             a much better outcome than closing the tab. -->
        <p class="mt-6 text-center">
          <button
            @click="skipToPractice"
            class="text-sm text-gray-400 hover:text-emerald-600 underline underline-offset-2 transition-colors"
          >
            Skip ahead to practice &rarr;
          </button>
        </p>
      </template>
    </main>
  </div>

  <PracticeSession
    v-else
    :exercise-url="exerciseUrl"
    :back-url="backUrl"
    :demo-mode="true"
    :register-url="registerUrl"
    :unit-title="unitTitle"
  />
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { apiGet } from '../api/client'
import { track } from '../utils/analytics'
import LearnMode from './LearnMode.vue'
import PracticeSession from './PracticeSession.vue'

const props = defineProps({
  vocabUrl:    { type: String, default: null },
  exerciseUrl: { type: String, required: true },
  backUrl:     { type: String, required: true },
  registerUrl: { type: String, default: null },
  unitTitle:   { type: String, default: null },
})

// Cold ad-click visitors bail fast, so the mini-lesson teaches only a handful of
// words instead of the whole unit. The server sends the curated subset (and
// filters exercises to match); this cap is just a guard against drift.
const DEMO_WORD_COUNT = 6

const phase = ref('lesson')
const vocab = ref([])
const loading = ref(true)
const wordsSeen = ref(0)

// Move from the mini-lesson into the exercises, recording the funnel step so we
// can see how many demo visitors get past the "learn the words" screen.
function startPractice() {
  track('demo_exercises_started', { unit: props.unitTitle })
  phase.value = 'practice'
}

// Per-word funnel breadcrumbs so we can see exactly where in the mini-lesson
// visitors bail (demo_started and demo_exercises_started only bracket it).
function onLessonProgress({ word, total }) {
  wordsSeen.value = word
  track('demo_lesson_progress', { word, total, unit: props.unitTitle })
}

function skipToPractice() {
  track('demo_lesson_skipped', { words_seen: wordsSeen.value, total: vocab.value.length, unit: props.unitTitle })
  startPractice()
}

onMounted(async () => {
  track('demo_started', { unit: props.unitTitle })
  if (!props.vocabUrl) {
    startPractice()
    return
  }
  try {
    const data = await apiGet(props.vocabUrl)
    vocab.value = (data.vocab || []).slice(0, DEMO_WORD_COUNT)
  } catch (e) {
    vocab.value = []
  } finally {
    loading.value = false
    // Nothing to teach (e.g. fetch failed) — go straight to the exercises.
    if (!vocab.value.length) startPractice()
  }
})
</script>
